import { FormEvent } from 'react';
import { Edit2, RefreshCw, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { getMaterialAriaSort, getMaterialSortIcon } from '../../utils/material.helpers';
import { Material, MaterialSortKey } from '../../types';

interface MaterialsPageProps {
  // Materials state from useMaterials hook
  filters: {
    search: string;
    status: 'all' | 'active' | 'inactive';
  };
  setFilters: React.Dispatch<React.SetStateAction<{
    search: string;
    status: 'all' | 'active' | 'inactive';
  }>>;
  loading: boolean;
  sortedMaterials: Material[];
  materialsSort: {
    column: MaterialSortKey;
    direction: 'asc' | 'desc';
  };
  toggleMaterialsSort: (column: MaterialSortKey) => void;
  toggleMaterialStatus: (id: string) => Promise<{ success: boolean; error?: string }>;
  materialDialogOpen: boolean;
  setMaterialDialogOpen: (open: boolean) => void;
  selectedMaterial: Material | null;
  materialCode: string;
  setMaterialCode: (code: string) => void;
  materialName: string;
  setMaterialName: (name: string) => void;
  materialDensity: string;
  setMaterialDensity: (density: string) => void;
  materialPricePerKg: string;
  setMaterialPricePerKg: (price: string) => void;
  materialActive: boolean;
  setMaterialActive: (active: boolean) => void;
  materialAllThicknesses: boolean;
  setMaterialAllThicknesses: (all: boolean) => void;
  materialThicknessInput: string;
  setMaterialThicknessInput: (input: string) => void;
  materialDimensionLimits: string;
  setMaterialDimensionLimits: (limits: string) => void;
  materialSaving: boolean;
  openMaterialDialog: (material?: Material) => void;

  // Auth state
  refreshing: boolean;

  // Permissions
  canEdit: boolean;

  // Handlers
  attemptRefresh: () => void;
  fetchMaterials: () => Promise<{ success: boolean; error?: string }>;
  handleCreateMaterial: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateMaterial: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

export function MaterialsPage({
  filters,
  setFilters,
  loading,
  sortedMaterials,
  materialsSort,
  toggleMaterialsSort,
  toggleMaterialStatus,
  materialDialogOpen,
  setMaterialDialogOpen,
  selectedMaterial,
  materialCode,
  setMaterialCode,
  materialName,
  setMaterialName,
  materialDensity,
  setMaterialDensity,
  materialPricePerKg,
  setMaterialPricePerKg,
  materialActive,
  setMaterialActive,
  materialAllThicknesses,
  setMaterialAllThicknesses,
  materialThicknessInput,
  setMaterialThicknessInput,
  materialDimensionLimits,
  setMaterialDimensionLimits,
  materialSaving,
  openMaterialDialog,
  refreshing,
  canEdit,
  attemptRefresh,
  fetchMaterials,
  handleCreateMaterial,
  handleUpdateMaterial
}: MaterialsPageProps) {
  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Orçamento</span>
        <span>/</span>
        <span className="font-medium text-foreground">Materiais do orçamento</span>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Materiais do orçamento</CardTitle>
            <CardDescription>
              Consulte e mantenha os materiais utilizados na geração de orçamento.
            </CardDescription>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Pesquisar código ou nome…"
                value={filters.search}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, search: event.target.value }))
                }
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <Button
                  type="button"
                  onClick={() => openMaterialDialog()}
                  className="flex-1 sm:flex-initial"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Novo Material</span>
                  <span className="sm:hidden">Novo</span>
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={attemptRefresh}
                disabled={refreshing}
                className="flex-1 sm:flex-initial"
              >
                <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
                <span className="hidden md:inline">{refreshing ? 'Renovando' : 'Renovar sessão'}</span>
                <span className="md:hidden">Renovar</span>
              </Button>
              <Button type="button" onClick={fetchMaterials} disabled={loading} className="flex-1 sm:flex-initial">
                <RefreshCw
                  className={cn('mr-2 h-4 w-4', loading && 'animate-spin')}
                />
                <span className="hidden md:inline">{loading ? 'Atualizando' : 'Recarregar'}</span>
                <span className="md:hidden">Atualizar</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <Tabs
            value={filters.status}
            onValueChange={(value) =>
              setFilters((current) => ({
                ...current,
                status: value as 'all' | 'active' | 'inactive'
              }))
            }
          >
            <TabsList>
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="active">Ativos</TabsTrigger>
              <TabsTrigger value="inactive">Inativos</TabsTrigger>
            </TabsList>
            <TabsContent value={filters.status}>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {sortedMaterials.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Nenhum material encontrado para os filtros atuais.
                  </p>
                ) : (
                  sortedMaterials.map((material) => (
                    <Card key={material.id} className="shadow-sm overflow-hidden">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-muted-foreground">{material.code}</div>
                            <div className="font-semibold break-words">{material.name}</div>
                            {material.availableThicknesses && material.availableThicknesses.length > 0 && (
                              <div className="text-xs text-muted-foreground mt-1 break-words">
                                Espessuras: {material.availableThicknesses.join(', ')}mm
                              </div>
                            )}
                          </div>
                          {material.deletedAt ? (
                            <Badge variant="destructive" className="flex-shrink-0">Excluído</Badge>
                          ) : material.active ? (
                            <Badge className="flex-shrink-0 bg-green-600 hover:bg-green-700 text-white">Ativo</Badge>
                          ) : (
                            <Badge className="flex-shrink-0 bg-red-600 hover:bg-red-700 text-white">Inativo</Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-xs text-muted-foreground">Densidade</div>
                            <div className="font-medium">{material.density.toFixed(2)} g/cm³</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Preço/kg</div>
                            <div className="font-medium">R$ {material.pricePerKg.toFixed(2)}</div>
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground pt-2 border-t">
                          Atualizado em: {new Date(material.updatedAt).toLocaleDateString('pt-BR')}
                        </div>

                        {canEdit && (
                          <div className="pt-2 border-t">
                            <Button
                              variant="default"
                              size="sm"
                              className="w-full"
                              onClick={() => openMaterialDialog(material)}
                            >
                              <Edit2 className="mr-2 h-4 w-4" />
                              Editar material
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead aria-sort={getMaterialAriaSort('code', materialsSort.column, materialsSort.direction)}>
                        <button
                          type="button"
                          onClick={() => toggleMaterialsSort('code')}
                          className="flex w-full items-center gap-1 bg-transparent text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          Código
                          {getMaterialSortIcon(materialsSort.column === 'code', materialsSort.direction)}
                        </button>
                      </TableHead>
                      <TableHead aria-sort={getMaterialAriaSort('name', materialsSort.column, materialsSort.direction)}>
                        <button
                          type="button"
                          onClick={() => toggleMaterialsSort('name')}
                          className="flex w-full items-center gap-1 bg-transparent text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          Nome
                          {getMaterialSortIcon(materialsSort.column === 'name', materialsSort.direction)}
                        </button>
                      </TableHead>
                      <TableHead
                        className="hidden text-right xl:table-cell"
                        aria-sort={getMaterialAriaSort('density', materialsSort.column, materialsSort.direction)}
                      >
                        <button
                          type="button"
                          onClick={() => toggleMaterialsSort('density')}
                          className="ml-auto flex w-full items-center justify-end gap-1 bg-transparent text-right focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          Densidade
                          {getMaterialSortIcon(materialsSort.column === 'density', materialsSort.direction)}
                        </button>
                      </TableHead>
                      <TableHead
                        className="hidden text-right lg:table-cell"
                        aria-sort={getMaterialAriaSort('pricePerKg', materialsSort.column, materialsSort.direction)}
                      >
                        <button
                          type="button"
                          onClick={() => toggleMaterialsSort('pricePerKg')}
                          className="ml-auto flex w-full items-center justify-end gap-1 bg-transparent text-right focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          Preço (R$/kg)
                          {getMaterialSortIcon(
                            materialsSort.column === 'pricePerKg',
                            materialsSort.direction
                          )}
                        </button>
                      </TableHead>
                      <TableHead aria-sort={getMaterialAriaSort('status', materialsSort.column, materialsSort.direction)}>
                        <button
                          type="button"
                          onClick={() => toggleMaterialsSort('status')}
                          className="flex w-full items-center gap-1 bg-transparent text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          Status
                          {getMaterialSortIcon(materialsSort.column === 'status', materialsSort.direction)}
                        </button>
                      </TableHead>
                      <TableHead
                        className="hidden text-right md:table-cell"
                        aria-sort={getMaterialAriaSort('updatedAt', materialsSort.column, materialsSort.direction)}
                      >
                        <button
                          type="button"
                          onClick={() => toggleMaterialsSort('updatedAt')}
                          className="ml-auto flex w-full items-center justify-end gap-1 bg-transparent text-right focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          Atualizado em
                          {getMaterialSortIcon(
                            materialsSort.column === 'updatedAt',
                            materialsSort.direction
                          )}
                        </button>
                      </TableHead>
                      {canEdit ? <TableHead className="text-right">Ações</TableHead> : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedMaterials.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canEdit ? 7 : 6} className="text-center text-sm">
                          Nenhum material encontrado para os filtros atuais.
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedMaterials.map((material) => (
                        <TableRow key={material.id}>
                          <TableCell className="font-medium">{material.code}</TableCell>
                          <TableCell>{material.name}</TableCell>
                          <TableCell className="hidden text-right xl:table-cell">
                            {material.density.toFixed(2)}
                          </TableCell>
                          <TableCell className="hidden text-right lg:table-cell">
                            {material.pricePerKg.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {material.deletedAt ? (
                              <Badge variant="destructive">Excluído</Badge>
                            ) : material.active ? (
                              <Badge className="bg-green-600 hover:bg-green-700 text-white">Ativo</Badge>
                            ) : (
                              <Badge className="bg-red-600 hover:bg-red-700 text-white">Inativo</Badge>
                            )}
                          </TableCell>
                          <TableCell className="hidden text-right md:table-cell text-xs text-muted-foreground">
                            {new Date(material.updatedAt).toLocaleDateString('pt-BR')}
                          </TableCell>
                          {canEdit ? (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openMaterialDialog(material)}
                                title="Editar material"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Material Form Dialog */}
      <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedMaterial ? 'Editar Material' : 'Novo Material'}
            </DialogTitle>
            <DialogDescription>
              {selectedMaterial
                ? 'Atualize as informações do material.'
                : 'Preencha os dados para criar um novo material.'}
            </DialogDescription>
          </DialogHeader>

          <form
            className="space-y-4"
            onSubmit={selectedMaterial ? handleUpdateMaterial : handleCreateMaterial}
          >
            <div className="space-y-2">
              <Label htmlFor="material-code">Código</Label>
              <Input
                id="material-code"
                value={materialCode}
                onChange={(event) => setMaterialCode(event.target.value)}
                placeholder="Ex: ACR-TRANS-3MM"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="material-name">Nome</Label>
              <Input
                id="material-name"
                value={materialName}
                onChange={(event) => setMaterialName(event.target.value)}
                placeholder="Ex: Acrílico Transparente 3mm"
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="material-density">Densidade (g/cm³)</Label>
                <Input
                  id="material-density"
                  type="number"
                  step="0.01"
                  min="0"
                  value={materialDensity}
                  onChange={(event) => setMaterialDensity(event.target.value)}
                  placeholder="Ex: 1.19"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="material-price">Preço (R$/kg)</Label>
                <Input
                  id="material-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={materialPricePerKg}
                  onChange={(event) => setMaterialPricePerKg(event.target.value)}
                  placeholder="Ex: 45.50"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label
                htmlFor="material-active"
                className="text-sm font-normal"
              >
                Material ativo (disponível para orçamentos)
              </Label>
              <Switch
                id="material-active"
                checked={materialActive}
                onCheckedChange={(checked) =>
                  setMaterialActive(checked === true)
                }
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="material-all-thicknesses"
                  checked={materialAllThicknesses}
                  onCheckedChange={(checked) =>
                    setMaterialAllThicknesses(checked === true)
                  }
                />
                <Label
                  htmlFor="material-all-thicknesses"
                  className="text-sm font-normal cursor-pointer"
                >
                  Todas as espessuras disponíveis
                </Label>
              </div>
              {!materialAllThicknesses && (
                <div className="space-y-2">
                  <Label htmlFor="material-thicknesses">
                    Espessuras permitidas (em mm)
                  </Label>
                  <Input
                    id="material-thicknesses"
                    value={materialThicknessInput}
                    onChange={(event) => setMaterialThicknessInput(event.target.value)}
                    placeholder="Ex: 2,3,4,5,6,10"
                  />
                  <p className="text-xs text-muted-foreground">
                    Separe as espessuras por vírgula, usando apenas números. Ex: 2,4,6,10
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="material-dimension-limits">
                Limites de Dimensão por Espessura (Opcional)
              </Label>
              <Input
                id="material-dimension-limits"
                value={materialDimensionLimits}
                onChange={(event) => setMaterialDimensionLimits(event.target.value)}
                placeholder="Ex: 122x244:2,3|300x200:4,6,8,10"
              />
              <p className="text-xs text-muted-foreground">
                Formato: larguraxaltura:espessuras. Para múltiplas configurações, separe com | (pipe).
                Ex: 122x244:2,3|300x200:4,6,8,10|100x200:12,15
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMaterialDialogOpen(false)}
                disabled={materialSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={materialSaving}>
                {materialSaving
                  ? 'Salvando…'
                  : selectedMaterial
                  ? 'Atualizar'
                  : 'Criar Material'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
